import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { Observable, catchError, filter, map, of, switchMap } from 'rxjs';
import { ConnectFile } from 'trimble-connect-workspace-api';
import { IngestionSource } from '../client-header/client-header.models';
import { ClientHeaderService } from '../client-header/client-header.service';
import { injectLogger } from '../logging/logger-injection';
import { ScandataModel } from '../scandata/scandata.models';
import { ScandataService } from '../scandata/scandata.service';
import { GET_SCAN_PROJECT_URL } from '../utils/get-scan-project-url';
import { AddTilingWorkflow, RemoveTilingWorkflow } from '../workflow/workflow.actions';
import { ConnectIngestion, ImportFile, ImportStatus } from './models/connect-ingestion';
import { ExternalFileId } from './models/external-file-id';

@Injectable({
  providedIn: 'root',
})
export class ConnectIngestionService {
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private logger = injectLogger('ConnectIngestionService');

  constructor(
    private http: HttpClient,
    private scandataService: ScandataService,
    private store: Store,
    private clientHeaderService: ClientHeaderService,
  ) {}

  public createIngestion(file: ConnectFile, downloadUrl: string, ingestionSource: IngestionSource) {
    const externalFileId = new ExternalFileId(file).id;
    if (isNil(externalFileId)) throw new Error('ExternalFileId is not defined');

    return this.store.dispatch(AddTilingWorkflow).pipe(
      switchMap(() => this.getScanForConnectFile(file, true)),
      switchMap((scan) =>
        isDefined(scan)
          ? of(scan).pipe(map((scan) => ({ scan, exist: true })))
          : this.createPointcloud(file.name, externalFileId, ingestionSource).pipe(
              map((scan) => ({ scan, exist: false })),
            ),
      ),
      switchMap(({ scan, exist }) =>
        this.createImport(scan.id, scan.name, downloadUrl, ingestionSource).pipe(
          map((importStatus) => {
            // Temporary logging to test the case where more than one import is created
            // for a new tiling operation with the same pointcloud id
            if (exist) {
              this.logger.warn(
                `Connect Create Ingestion pointcloud ${scan.id} status ${scan.status} exists`,
                {
                  file,
                  scan,
                  importStatus,
                },
              );
            }

            return scan;
          }),
        ),
      ),
      switchMap(() => this.updateScanInStore(file)),
      switchMap(() => this.store.dispatch(RemoveTilingWorkflow)),
      catchError((err) => {
        this.store.dispatch(RemoveTilingWorkflow);
        this.logger.error(`Connect Create Ingestion error (${file.id} ${file.name})`, {}, err);
        throw err;
      }),
    );
  }

  public getIngestion(file: ConnectFile): Observable<ConnectIngestion> {
    return this.getScanForConnectFile(file).pipe(
      filter((scan): scan is ScandataModel => isDefined(scan)),
      map((scan) => ({ file, scan })),
    );
  }

  public getScanForConnectFile(file: ConnectFile, useOnlyHash = false) {
    const externalFileId = new ExternalFileId(file);
    return this.scandataService.getScanByExternalFileId(externalFileId, useOnlyHash);
  }

  private createImport(
    pointcloudId: string,
    name: string,
    sourceUrl: string,
    ingestionSource: IngestionSource,
  ) {
    const importFile: ImportFile = {
      sourceUrl,
      targetFileName: name,
    };

    return this.getScanProjectUrl$(`/pointclouds/${pointcloudId}/imports`).pipe(
      switchMap((url) =>
        this.http.post<ImportStatus>(
          url,
          {
            files: [importFile],
          },
          { headers: this.clientHeaderService.getClientHeaders(ingestionSource) },
        ),
      ),
    );
  }

  private createPointcloud(name: string, externalFileId: string, ingestionSource: IngestionSource) {
    return this.getScanProjectUrl$('/pointclouds').pipe(
      switchMap((url) =>
        this.http.post<ScandataModel>(
          url,
          {
            name,
            externalFileId,
            multiImportsAllowed: false,
            autoIngestAfterImports: true,
          },
          { headers: this.clientHeaderService.getClientHeaders(ingestionSource) },
        ),
      ),
    );
  }

  private updateScanInStore(file: ConnectFile) {
    // scandataService.getScanByExternalFileId updates scan with
    // necessary properties and upserts into the store
    return this.getScanForConnectFile(file);
  }
}
